import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import AuthContext from '../../context/authContext';
import useIndividualDiscountCode from '../../hooks/useIndividualDiscountCode';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const MAX_CODE_LENGTH = 20;
const MIN_SUFFIX_LENGTH = 4;
const DEFAULT_SUFFIX_LENGTH = 9;

const STATUS_CHIP = {
  active: { label: 'Aktiv', color: 'success' },
  redeemed: { label: 'Eingelöst', color: 'info' },
  revoked: { label: 'Widerrufen', color: 'error' },
  expired: { label: 'Abgelaufen', color: 'default' },
};

const SOURCE_LABELS = { draw: 'Gezogen', batch: 'Batch', api: 'API' };

// Mirrors the backend code schema: <prefix><random suffix>, alphanumeric, max 20 chars total
const resolveCodeSchema = parent => {
  const rawPrefix = parent?.individualCodePrefix || parent?.code || '';
  const prefix = String(rawPrefix).replace(/[^a-zA-Z0-9]/g, '');
  const prefixTooLong = prefix.length > MAX_CODE_LENGTH - MIN_SUFFIX_LENGTH;
  const requested = parent?.individualCodeSuffixLength || DEFAULT_SUFFIX_LENGTH;
  const suffixLength = Math.max(
    MIN_SUFFIX_LENGTH,
    Math.min(requested, MAX_CODE_LENGTH - prefix.length)
  );
  return { prefix, suffixLength, prefixTooLong };
};

function IndividualDiscountCodesPanel() {
  const authCtx = useContext(AuthContext);
  const {
    isLoading,
    getDiscountCodes,
    getIndividualCodes,
    generateIndividualCodes,
    revokeIndividualCodes,
    reactivateIndividualCodes,
    downloadIndividualCodesCsv,
  } = useIndividualDiscountCode();

  const [allParents, setAllParents] = useState([]);
  // 'lists' = partner code lists (bulk generation), 'draw' = employee-drawn codes
  const [mechanism, setMechanism] = useState('lists');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [codes, setCodes] = useState([]);
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // { mode: 'selection' | 'all' }
  const [busy, setBusy] = useState(false);

  const searchRef = useRef(null);
  const countRef = useRef(null);
  const batchLabelRef = useRef(null);
  const expiresAtRef = useRef(null);

  const canGenerate = authCtx.checkRight('discountCode:special:generateCodes');
  const canRevoke = authCtx.checkRight('discountCode:special:revokeCodes');

  // Only parents of the selected mechanism are offered — partner code lists and
  // employee-drawn codes are managed separately.
  const parents = allParents.filter(dc =>
    mechanism === 'lists' ? dc.allowIndividualCodeLists : dc.allowIndividualCodes
  );

  const selectedParent = parents.find(p => p._id === selectedParentId) || null;
  const schema = selectedParent ? resolveCodeSchema(selectedParent) : null;

  // Redemption of individual codes is additionally bound to the parent's state
  const redeemWindowOver = Boolean(
    selectedParent?.redeemValidTo && dayjs(selectedParent.redeemValidTo).isBefore(dayjs())
  );
  const parentInactive = selectedParent ? selectedParent.active === false : false;
  const redeemWindowText = selectedParent
    ? [
        selectedParent.redeemValidFrom
          ? `einlösbar ab ${dayjs(selectedParent.redeemValidFrom).format('DD.MM.YYYY')}`
          : null,
        selectedParent.redeemValidTo
          ? `einlösbar bis ${dayjs(selectedParent.redeemValidTo).format('DD.MM.YYYY')}`
          : null,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const loadParents = useCallback(async () => {
    try {
      const data = await getDiscountCodes();
      if (Array.isArray(data)) {
        setAllParents(data.filter(dc => dc.allowIndividualCodeLists || dc.allowIndividualCodes));
      }
    } catch (e) {
      // Toast handled by the hook
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCodes = useCallback(async () => {
    if (!selectedParentId) return;
    try {
      const data = await getIndividualCodes(selectedParentId, {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        search: appliedSearch || undefined,
        batchId: batchFilter || undefined,
      });
      if (data) {
        setCodes(Array.isArray(data.codes) ? data.codes : []);
        setTotal(data.total || 0);
        setCounts(data.counts || null);
        setSelectedIds([]);
      }
    } catch (e) {
      // Toast handled by the hook
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParentId, page, rowsPerPage, statusFilter, sourceFilter, appliedSearch, batchFilter]);

  useEffect(() => {
    loadParents();
  }, [loadParents]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const currentFilterPayload = () => ({
    source: sourceFilter || undefined,
    search: appliedSearch || undefined,
    batchId: batchFilter || undefined,
  });

  const handleApplySearch = () => {
    setPage(0);
    setAppliedSearch(searchRef.current?.value?.trim() || '');
  };

  const handleGenerate = async () => {
    const count = parseInt(countRef.current?.value, 10);
    if (!count || count < 1 || count > 50000) {
      toast.error('Bitte eine Anzahl zwischen 1 und 50.000 angeben');
      return;
    }
    setBusy(true);
    try {
      const result = await generateIndividualCodes(selectedParentId, {
        count,
        batchLabel: batchLabelRef.current?.value?.trim() || undefined,
        expiresAt: expiresAtRef.current?.value || undefined,
      });
      if (result) {
        toast.success(
          `${result.count} individuelle Codes generiert (Batch ${result.batchId || '–'})`
        );
        (result.warnings || []).forEach(w => toast.warn(w, { autoClose: 12000 }));
        setGenerateOpen(false);
        setPage(0);
        await loadCodes();
      }
    } catch (e) {
      // Toast handled by the hook
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setBusy(true);
    try {
      const payload =
        confirmRevoke.mode === 'all'
          ? { all: true, filter: currentFilterPayload() }
          : { codeIds: selectedIds };
      const result = await revokeIndividualCodes(selectedParentId, payload);
      if (result) {
        toast.success(`${result.revokedCount} Code(s) widerrufen`);
        setConfirmRevoke(null);
        await loadCodes();
      }
    } catch (e) {
      // Toast handled by the hook
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      const result = await reactivateIndividualCodes(selectedParentId, selectedIds);
      if (result) {
        toast.success(`${result.reactivatedCount} Code(s) reaktiviert`);
        await loadCodes();
      }
    } catch (e) {
      // Toast handled by the hook
    } finally {
      setBusy(false);
    }
  };

  const handleCsvDownload = async codesOnly => {
    try {
      const blob = await downloadIndividualCodesCsv(selectedParentId, {
        status: statusFilter || undefined,
        format: codesOnly ? 'codesOnly' : undefined,
        ...currentFilterPayload(),
      });
      if (!blob) return;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `individuelle-codes-${selectedParent?.code || 'export'}-${dayjs().format(
        'YYYYMMDD-HHmm'
      )}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      // Toast handled by the hook
    }
  };

  const copyToClipboard = code => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success('Code in Zwischenablage kopiert');
    });
  };

  const toggleSelect = id => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === codes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(codes.map(c => c._id));
    }
  };

  const formatDate = d => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '–');

  const formatUser = u => {
    if (!u) return '';
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || '';
  };

  const selectedRevokedCount = codes.filter(
    c => selectedIds.includes(c._id) && c.status === 'revoked'
  ).length;

  const handleMechanismChange = value => {
    if (!value || value === mechanism) return;
    setMechanism(value);
    setSelectedParentId('');
    setCodes([]);
    setCounts(null);
    setTotal(0);
    setPage(0);
    setStatusFilter('');
    setSourceFilter('');
    setAppliedSearch('');
    setBatchFilter('');
    setSelectedIds([]);
  };

  return (
    <Box>
      <ToggleButtonGroup
        value={mechanism}
        exclusive
        size='small'
        onChange={(e, v) => handleMechanismChange(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value='lists'>Partner-Listen</ToggleButton>
        <ToggleButton value='draw'>MA-Ziehung</ToggleButton>
      </ToggleButtonGroup>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems='flex-start'>
        <TextField
          select
          size='small'
          label='Übergeordneter Rabattcode'
          value={selectedParentId}
          onChange={e => {
            setSelectedParentId(e.target.value);
            setPage(0);
            setBatchFilter('');
            setAppliedSearch('');
          }}
          sx={{ minWidth: 280 }}
          helperText={
            parents.length === 0
              ? mechanism === 'lists'
                ? 'Kein Rabattcode mit aktivierter Partner-Liste vorhanden — aktiviere "Individuelle Codes: Partner-Liste erlauben" am gewünschten Rabattcode.'
                : 'Kein Rabattcode mit aktivierter MA-Ziehung vorhanden.'
              : undefined
          }
        >
          {parents.map(p => (
            <MenuItem key={p._id} value={p._id}>
              {p.code} ({p.globalPercent}%)
            </MenuItem>
          ))}
        </TextField>

        {schema && (
          <Alert
            severity={
              schema.prefixTooLong || redeemWindowOver || parentInactive ? 'error' : 'info'
            }
            sx={{ flex: 1 }}
          >
            {parentInactive
              ? 'Dieser Rabattcode ist deaktiviert — seine individuellen Codes sind aktuell nicht einlösbar.'
              : redeemWindowOver
                ? `Das Einlösefenster dieses Rabattcodes ist am ${dayjs(
                    selectedParent.redeemValidTo
                  ).format('DD.MM.YYYY')} abgelaufen — Codes sind nicht mehr einlösbar und es können keine neuen generiert werden. Passe "Einlösbar bis" am Rabattcode an.`
                : schema.prefixTooLong
                  ? `Das Präfix "${schema.prefix}" ist zu lang – es müssen mindestens ${MIN_SUFFIX_LENGTH} Zufallszeichen in ${MAX_CODE_LENGTH} Zeichen passen. Bitte in der Rabattcode-Konfiguration ein kürzeres Präfix hinterlegen.`
                  : `Code-Schema: ${schema.prefix}••• (${schema.prefix.length} + ${schema.suffixLength} Zufallszeichen, max. ${MAX_CODE_LENGTH} Zeichen, keine Sonderzeichen). Jeder Code ist nur einmal einlösbar.${
                      redeemWindowText ? ` Einlösefenster: ${redeemWindowText}.` : ''
                    }`}
          </Alert>
        )}
      </Stack>

      {selectedParentId && (
        <>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
              <TextField
                select
                size='small'
                label='Status'
                value={statusFilter}
                onChange={e => {
                  setPage(0);
                  setStatusFilter(e.target.value);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value=''>Alle</MenuItem>
                <MenuItem value='active'>Aktiv</MenuItem>
                <MenuItem value='redeemed'>Eingelöst</MenuItem>
                <MenuItem value='revoked'>Widerrufen</MenuItem>
                <MenuItem value='expired'>Abgelaufen</MenuItem>
              </TextField>
              <TextField
                select
                size='small'
                label='Quelle'
                value={sourceFilter}
                onChange={e => {
                  setPage(0);
                  setSourceFilter(e.target.value);
                }}
                sx={{ minWidth: 130 }}
              >
                <MenuItem value=''>Alle</MenuItem>
                <MenuItem value='batch'>Batch</MenuItem>
                <MenuItem value='draw'>Gezogen</MenuItem>
                <MenuItem value='api'>API</MenuItem>
              </TextField>
              <TextField
                size='small'
                label='Code suchen'
                inputRef={searchRef}
                defaultValue=''
                onKeyDown={e => {
                  if (e.key === 'Enter') handleApplySearch();
                }}
              />
              <Button variant='outlined' size='small' onClick={handleApplySearch}>
                Suchen
              </Button>
              {batchFilter && (
                <Chip
                  label={`Batch: ${batchFilter}`}
                  onDelete={() => {
                    setPage(0);
                    setBatchFilter('');
                  }}
                  size='small'
                />
              )}
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction='row' spacing={1} flexWrap='wrap'>
              {canGenerate && mechanism === 'lists' && selectedParent?.allowIndividualCodeLists && (
                <Button
                  variant='contained'
                  size='small'
                  startIcon={<AddIcon />}
                  disabled={schema?.prefixTooLong || redeemWindowOver || parentInactive}
                  onClick={() => setGenerateOpen(true)}
                >
                  Codes generieren
                </Button>
              )}
              <Button
                variant='outlined'
                size='small'
                startIcon={<FileDownloadIcon />}
                onClick={() => handleCsvDownload(false)}
              >
                CSV-Export
              </Button>
              <Tooltip title='Nur die Codes, eine pro Zeile – z. B. für den Upload beim Partner'>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleCsvDownload(true)}
                >
                  Nur Codes (CSV)
                </Button>
              </Tooltip>
            </Stack>
          </Stack>

          {counts && (
            <Stack direction='row' spacing={1} sx={{ mb: 2 }} flexWrap='wrap'>
              <Chip label={`Gesamt: ${counts.total}`} size='small' />
              <Chip label={`Aktiv: ${counts.active}`} color='success' size='small' variant='outlined' />
              <Chip label={`Eingelöst: ${counts.redeemed}`} color='info' size='small' variant='outlined' />
              <Chip label={`Widerrufen: ${counts.revoked}`} color='error' size='small' variant='outlined' />
              <Chip label={`Abgelaufen: ${counts.expired}`} size='small' variant='outlined' />
            </Stack>
          )}

          {canRevoke && (
            <Stack direction='row' spacing={1} sx={{ mb: 2 }} flexWrap='wrap'>
              <Button
                variant='outlined'
                color='error'
                size='small'
                startIcon={<BlockIcon />}
                disabled={selectedIds.length === 0 || busy}
                onClick={() => setConfirmRevoke({ mode: 'selection' })}
              >
                Auswahl widerrufen ({selectedIds.length})
              </Button>
              <Button
                variant='outlined'
                color='error'
                size='small'
                startIcon={<BlockIcon />}
                disabled={busy || !counts || counts.active === 0}
                onClick={() => setConfirmRevoke({ mode: 'all' })}
              >
                Alle aktiven widerrufen
              </Button>
              <Button
                variant='outlined'
                size='small'
                startIcon={<RestoreIcon />}
                disabled={selectedRevokedCount === 0 || busy}
                onClick={handleReactivate}
              >
                Auswahl reaktivieren ({selectedRevokedCount})
              </Button>
            </Stack>
          )}

          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={codes.length > 0 && selectedIds.length === codes.length}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < codes.length}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Quelle</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Gültig bis</TableCell>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell>Erstellt für</TableCell>
                  <TableCell>Eingelöst am</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && codes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && codes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      <Typography variant='body2' color='text.secondary'>
                        Keine individuellen Codes vorhanden
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {codes.map(c => {
                  const chip = STATUS_CHIP[c.status] || { label: c.status, color: 'default' };
                  return (
                    <TableRow key={c._id} hover selected={selectedIds.includes(c._id)}>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selectedIds.includes(c._id)}
                          onChange={() => toggleSelect(c._id)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {c.code}
                        <IconButton size='small' onClick={() => copyToClipboard(c.code)}>
                          <ContentCopyIcon fontSize='inherit' />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip label={chip.label} color={chip.color} size='small' />
                      </TableCell>
                      <TableCell>{SOURCE_LABELS[c.source] || c.source}</TableCell>
                      <TableCell>
                        {c.batchId ? (
                          <Tooltip title={c.batchLabel || c.batchId}>
                            <Chip
                              label={c.batchLabel || c.batchId}
                              size='small'
                              variant='outlined'
                              onClick={() => {
                                setPage(0);
                                setBatchFilter(c.batchId);
                              }}
                            />
                          </Tooltip>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(c.expiresAt)}</TableCell>
                      <TableCell>{formatDate(c.createdAt)}</TableCell>
                      <TableCell>
                        {formatUser(c.createdForUserId) || c.assignedToEmail || '–'}
                      </TableCell>
                      <TableCell>{formatDate(c.redeemedAt)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component='div'
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage='Zeilen pro Seite'
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
            />
          </TableContainer>
        </>
      )}

      {/* Generate dialog */}
      <Dialog open={generateOpen} onClose={() => !busy && setGenerateOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Individuelle Codes generieren</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {schema &&
              `Schema: ${schema.prefix} + ${schema.suffixLength} Zufallszeichen. Die Bedingungen (Rabatt, Filialen, Zeitfenster …) entsprechen dem übergeordneten Rabattcode "${selectedParent?.code}". Jeder Code ist nur einmal einlösbar.`}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label='Anzahl (max. 50.000)'
              type='number'
              inputRef={countRef}
              defaultValue={1000}
              inputProps={{ min: 1, max: 50000 }}
              fullWidth
            />
            <TextField
              label='Batch-Label (optional)'
              inputRef={batchLabelRef}
              placeholder='z. B. Corporate Benefits Juli 2026'
              fullWidth
            />
            <TextField
              label='Gültig bis (optional)'
              type='date'
              inputRef={expiresAtRef}
              InputLabelProps={{ shrink: true }}
              helperText={
                selectedParent?.individualCodeExpiryDays
                  ? `Leer = ${selectedParent.individualCodeExpiryDays} Tage ab Erstellung`
                  : 'Leer = 30 Tage ab Erstellung'
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant='contained' onClick={handleGenerate} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Generieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <Dialog open={Boolean(confirmRevoke)} onClose={() => !busy && setConfirmRevoke(null)}>
        <DialogTitle>Codes widerrufen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmRevoke?.mode === 'all'
              ? 'Alle noch nicht eingelösten Codes, die dem aktuellen Filter entsprechen (inkl. bereits abgelaufener), werden widerrufen und können nicht mehr eingelöst werden. Bereits eingelöste Codes bleiben unverändert.'
              : `${selectedIds.length} ausgewählte Code(s) werden widerrufen und können nicht mehr eingelöst werden. Bereits eingelöste Codes bleiben unverändert.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant='contained' color='error' onClick={handleRevoke} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Widerrufen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default IndividualDiscountCodesPanel;
